"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { createLogicTest, type LogicQuestion } from "@/lib/atsa/verbal";
import { formatTime } from "@/lib/atsa/utils";

type Phase = "options" | "running" | "result";

export default function LogicalReasoningPage() {
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [questionCount, setQuestionCount] = useState(10);
  const [durationSeconds, setDurationSeconds] = useState(20 * 60);
  const [questions, setQuestions] = useState<LogicQuestion[]>([]);
  const [answers, setAnswers] = useState<Array<number | null>>([]);
  const [current, setCurrent] = useState(0);
  const [remaining, setRemaining] = useState(20 * 60);

  const effectiveCount = mode === "simulation" ? ATSA_PROFILE_2026.logic.questions : questionCount;
  const effectiveDuration = mode === "simulation" ? ATSA_PROFILE_2026.logic.seconds : durationSeconds;

  const begin = useCallback(() => {
    const test = createLogicTest(effectiveCount, Date.now());
    setQuestions(test);
    setAnswers(Array.from({ length: test.length }, () => null));
    setCurrent(0);
    setRemaining(effectiveDuration);
    setPhase("running");
  }, [effectiveCount, effectiveDuration]);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          setPhase("result");
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  function choose(index: number) {
    setAnswers((currentAnswers) => currentAnswers.map((answer, answerIndex) => answerIndex === current ? index : answer));
  }

  function next() {
    if (current + 1 < questions.length) setCurrent((value) => value + 1);
    else setPhase("result");
  }

  const question = questions[current];
  const correct = useMemo(() => questions.reduce((sum, item, index) => sum + (answers[index] === item.answer ? 1 : 0), 0), [answers, questions]);
  const activeSimulation = mode === "simulation" && phase === "running";

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Logical Reasoning"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" ? `Question ${current + 1} of ${questions.length} · ${formatTime(remaining)}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? begin : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 7</p>
              <h2>Logical Reasoning</h2>
              <p>Apply ordering and scheduling constraints under severe time pressure. Every generated item is validated against all possible schedules before display.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setQuestionCount(15);
                setDurationSeconds(20 * 60);
              }
            }} />
            {mode === "training" && (
              <div className="options-grid">
                <div className="profile-block">
                  <span className="profile-label">Difficulty profile</span>
                  <div className="profile-row">
                    {([['Light', 'Warm-up', 10, 1500, false], ['Standard', 'Test-like', 15, 1200, true], ['Overload', 'Harder', 18, 600, false]] as const).map(([label, tag, qc, secs, std]) => (
                      <button type="button" key={label} className={`profile-chip ${questionCount === qc && durationSeconds === secs ? "active" : ""}`} onClick={() => { setQuestionCount(qc); setDurationSeconds(secs); }}>
                        <span className="tag">{tag}</span>{label}{std ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="control-group"><label htmlFor="logic-count">Questions</label><select id="logic-count" value={questionCount} onChange={(event) => setQuestionCount(Number(event.target.value))}><option value={5}>5</option><option value={10}>10</option><option value={15}>15</option><option value={18}>18 overload</option></select></div>
                <div className="control-group"><label htmlFor="logic-time">Time limit</label><select id="logic-time" value={durationSeconds} onChange={(event) => setDurationSeconds(Number(event.target.value))}><option value={10 * 60}>10 minutes</option><option value={20 * 60}>20 minutes</option><option value={25 * 60}>25 minutes</option></select></div>
              </div>
            )}
            <ol className="instructions">
              <li>Fidelity simulation uses 15 questions in 20 minutes.</li>
              <li>Public sources also report approximately 18 questions, available as overload training.</li>
              <li>No scratchpad is provided.</li>
              <li>Fidelity mode commits each answer and does not permit backtracking.</li>
            </ol>
            <button className="primary-button" onClick={begin}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {phase === "running" && question && (
          <section className={activeSimulation ? "fidelity-logic" : "question-card logic-card"}>
            <div className="logic-setup">
              <p>{question.setup}</p>
              <p>The following conditions must all be satisfied:</p>
              <ul>{question.rules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
            </div>
            <h3 className="logic-prompt">{question.prompt}</h3>
            <div className="answer-list light-answer-list logic-options">
              {question.options.map((option, index) => (
                <label className={`answer-option ${answers[current] === index ? "selected" : ""}`} key={`${option}-${index}`}>
                  <input type="radio" name="logic-answer" checked={answers[current] === index} onChange={() => choose(index)} />
                  <strong>{String.fromCharCode(65 + index)}.</strong>
                  <span>{option}</span>
                </label>
              ))}
            </div>
            <div className="verbal-actions">
              {mode === "training" && current > 0 && <button className="secondary-button" onClick={() => setCurrent((value) => value - 1)}>Previous</button>}
              <button className="primary-button" disabled={answers[current] === null} onClick={next}>{current + 1 === questions.length ? "Finish" : "Next"}</button>
            </div>
          </section>
        )}

        {phase === "result" && (
          <>
            <Results
              section="Logical Reasoning"
              record={{ sectionSlug: "logical-reasoning", score: questions.length ? Math.round((correct / questions.length) * 100) : 0, scoreLabel: "Accuracy" }}
              title="Logical Reasoning Complete"
              metrics={[
                { label: "Correct", value: correct },
                { label: "Incorrect", value: answers.filter((answer) => answer !== null).length - correct },
                { label: "Unanswered", value: answers.filter((answer) => answer === null).length },
                { label: "Accuracy", value: `${questions.length ? Math.round((correct / questions.length) * 100) : 0}%` },
              ]}
              onAgain={begin}
              onOptions={() => setPhase("options")}
            />
            {mode === "training" && questions.length > 0 && (
              <section className="panel review-panel"><h3>Answer review</h3>{questions.map((item, index) => <div className={`review-item ${answers[index] === item.answer ? "correct" : "wrong"}`} key={item.id}><strong>{index + 1}. {item.prompt}</strong><p>{item.explanation}</p></div>)}</section>
            )}
          </>
        )}
      </TestChrome>
    </AppShell>
  );
}
